import React, { useEffect, useMemo } from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { getTaskQueryOptions } from 'src/services/tasks/taskQueries';
import {
  setIsCancelModalOpen,
  setJmrResults,
  setPageProgress,
  setRestrictionListItems,
  setSelectedRestrictions,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { JmrPageProgress } from 'src/utils/jmrUtils';
import {
  regionTasksAndGroupsPayloadToRestrictionOptionsList,
  setupLimitationReviewItems,
  setupPreselectedRestrictionOptions,
  setupRestrictionReviewItems,
} from 'src/utils/restrictionUtils';
import {
  regionTaskListToRestrictionItemTasks,
  setupPreselectedRestrictionListItems,
  setupRestrictionReviewItems as setupRestrictionReviewItemsLegacy,
} from 'src/utils/restrictionUtilsLegacy';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJMRPageWrapper } from '../pageLayout/pageLayout';

import BackgroundReview from './backgroundReview/backgroundReview';
import BackgroundReviewLegacy from './backgroundReview/backgroundReviewLegacy';
import PlacementSelectionsReview from './placementSelectionsReview/placementSelectionsReview';
import PlacementSelectionsReviewLegacy from './placementSelectionsReview/placementSelectionsReviewLegacy';
import LimitationsReview from './restrictionsReview/limitationsReview';
import RestrictionsReview from './restrictionsReview/restrictionsReview';
import RestrictionsReviewLegacy from './restrictionsReview/restrictionsReviewLegacy';

// **** Components **** //

/**
 * Default component
 */
const ReviewPage = () => {
  const dispatch = useAppDispatch();
  const { featureFlags, isFeatureFlagsLoaded } = useAppSelector((state) => state.user);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const {
    backgroundFormData,
    selectedRestrictions,
    selectedTasks,
    restrictionListItems,
    selectedLimitations,
    jmrFlowType,
  } = useAppSelector((state) => state.newJmrPage);
  const { shiftLength } = backgroundFormData;
  const {
    data: restrictionOptions,
    isSuccess: isTaskGroupsQuerySuccess,
    isLoading: isTaskGroupsLoading,
  } = useQuery({
    ...getTaskQueryOptions.regionTaskGroups(backgroundFormData.site?.regionId),
    enabled: !!backgroundFormData.site?.regionId && isFeatureFlagsLoaded && featureFlags.TASK_GROUPING,
    select: regionTasksAndGroupsPayloadToRestrictionOptionsList,
  });
  const {
    data: consolidatedRegionTasks,
    isSuccess: isTasksQuerySuccess,
    isLoading: isTasksLoading,
  } = useQuery({
    ...getTaskQueryOptions.regionTasks(backgroundFormData.site?.regionId),
    enabled: !!backgroundFormData.site?.regionId && isFeatureFlagsLoaded && !featureFlags.TASK_GROUPING,
    select: regionTaskListToRestrictionItemTasks,
  });

  // Check criteria to determine whether to use job modifications experience
  const isUsingJobModificationsExperience = useMemo(
    () =>
      featureFlags.JOB_MODIFICATIONS &&
      allowedSites.some((site) => site.site === backgroundFormData.siteLocation && site.jobModificationsEnabled),
    [featureFlags.JOB_MODIFICATIONS, allowedSites],
  );
  // Check criteria to determine whether to use non physical limitations
  const nonPhysicalLimitationsEnabled =
    featureFlags.NON_PHYSICAL_LIMITATIONS && backgroundFormData.site.nonPhysicalLimitationsEnabled;

  // Side effect: initialize preselected options when data is first available
  useEffect(() => {
    if (isFeatureFlagsLoaded) {
      if (featureFlags.TASK_GROUPING && isTaskGroupsQuerySuccess && !selectedRestrictions.length) {
        const items = setupPreselectedRestrictionOptions(selectedTasks, restrictionOptions, shiftLength);
        if (items.length) {
          dispatch(setSelectedRestrictions(items));
        }
      } else if (!featureFlags.TASK_GROUPING && isTasksQuerySuccess && !restrictionListItems.length) {
        const items = setupPreselectedRestrictionListItems(selectedTasks, consolidatedRegionTasks);
        if (items.length) {
          dispatch(setRestrictionListItems(items));
        }
      }
    }
  }, [
    isFeatureFlagsLoaded,
    featureFlags.TASK_GROUPING,
    restrictionOptions,
    consolidatedRegionTasks,
    selectedTasks,
    shiftLength,
    selectedRestrictions,
    restrictionListItems,
  ]);

  const restrictions = useMemo(() => {
    if (isFeatureFlagsLoaded) {
      if (featureFlags.TASK_GROUPING && selectedRestrictions.length) {
        return setupRestrictionReviewItems(selectedRestrictions, shiftLength);
      } else if (!featureFlags.TASK_GROUPING && restrictionListItems.length) {
        return setupRestrictionReviewItemsLegacy(restrictionListItems, shiftLength);
      }
    }
    return [];
  }, [isFeatureFlagsLoaded, featureFlags.TASK_GROUPING, selectedRestrictions, restrictionListItems, shiftLength]);

  const limitations = useMemo(() => {
    if (isFeatureFlagsLoaded) {
      return setupLimitationReviewItems(selectedLimitations);
    }
    return [];
  }, [isFeatureFlagsLoaded, selectedLimitations]);

  const goToRestrictions = () => {
    dispatch(setPageProgress(JmrPageProgress.Restrictions));
    dispatch(setJmrResults([]));
  };

  return (
    <NewJMRPageWrapper>
      {/* Main Body */}
      <NewJMRPageMainBody sm="90%" md="75%" lg="50%" title="Review Job Match">
        <LoadingSpinnerWrapper showSpinner={isTaskGroupsLoading || isTasksLoading}>
          {isUsingJobModificationsExperience ? (
            <>
              <Box spacingInset="400" />
              <BackgroundReview />
              <Box spacingInset="400" />
              <RestrictionsReview
                restrictionReviewItems={restrictions}
                jmrFlowType={jmrFlowType}
                onConfirm={goToRestrictions}
              />
              <Box spacingInset="400" />
              {nonPhysicalLimitationsEnabled ? (
                <>
                  <LimitationsReview
                    limitationReviewItems={limitations}
                    jmrFlowType={jmrFlowType}
                    onConfirm={goToRestrictions}
                  />
                  <Box spacingInset="400" />
                </>
              ) : null}
              <PlacementSelectionsReview />
            </>
          ) : (
            <>
              <Divider spacingBefore="500" spacingAfter="300" />
              <BackgroundReviewLegacy />
              <Divider spacingBefore="500" spacingAfter="100" />
              <RestrictionsReviewLegacy />
              <Divider spacingBefore="500" spacingAfter="100" />
              <PlacementSelectionsReviewLegacy />
            </>
          )}
        </LoadingSpinnerWrapper>
      </NewJMRPageMainBody>

      {/* Footer */}
      <NewJMRPageFooter>
        <Row spacing="400">
          <div></div>
          <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
            Cancel
          </Button>
          <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.JobMatches))}>
            Previous
          </Button>
          <Button
            data-cy="new-jmr__review__next"
            onClick={() => dispatch(setPageProgress(JmrPageProgress.SendForApproval))}
          >
            Next
          </Button>
        </Row>
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

// **** Export **** //

export default ReviewPage;
